import React, { useEffect, useRef, useState } from 'react';
import { verifyUser, loadUsers } from '../utils/auth';

export default function DesktopLogin({ open, onSuccess, onCancel }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [time, setTime] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsersState] = useState([]);
  const passwordRef = useRef(null);
  const usernameRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      const loaded = loadUsers();
      setUsersState(loaded);
      setUsername('');
      setPassword('');
      setMessage('');
      setShowPassword(false);
      setSelectedUser(null);

      // Listen for Ctrl+A to show admin login
      const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === 'a') {
          e.preventDefault();
          const adminUser = loaded.find(u => u.username === 'ymnode');
          if (adminUser) {
            selectUser(adminUser);
          }
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e && e.preventDefault();

    if (!username.trim()) {
      setMessage('Veuillez entrer votre nom d\'utilisateur');
      return;
    }
    if (!password) {
      setMessage('Veuillez entrer votre mot de passe');
      return;
    }

    setIsLoading(true);
    try {
      const ok = await verifyUser(username.trim(), password);
      if (ok) {
        sessionStorage.setItem('my-dashboard-authenticated', 'true');
        sessionStorage.setItem('my-dashboard-user', username.trim());
        setMessage('');
        onSuccess && onSuccess();
      } else {
        setMessage('Mot de passe incorrect');
        setPassword('');
        setTimeout(() => passwordRef.current && passwordRef.current.focus(), 100);
      }
    } catch (err) {
      console.error(err);
      setMessage('Erreur d\'authentification');
    } finally {
      setIsLoading(false);
    }
  }

  function selectUser(user) {
    setSelectedUser(user.username);
    setUsername(user.username);
    setPassword('');
    setMessage('');
    // If guest account, auto-login
    if (user.username === 'Invité' && user.hash === 'guest') {
      setTimeout(async () => {
        const ok = await verifyUser('Invité', '');
        if (ok) {
          sessionStorage.setItem('my-dashboard-authenticated', 'true');
          sessionStorage.setItem('my-dashboard-user', 'Invité');
          onSuccess && onSuccess();
        }
      }, 100);
      return;
    }
    setTimeout(() => passwordRef.current && passwordRef.current.focus(), 100);
  }

  const hours = String(time.getHours()).padStart(2, '0');
  const minutes = String(time.getMinutes()).padStart(2, '0');
  const dayName = time.toLocaleDateString('fr-FR', { weekday: 'long' });
  const dateFull = time.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={styles.container}>
      {/* Background avec blur */}
      <div style={styles.background} />
      
      {/* Coin supérieur gauche - Heure/Date */}
      <div style={styles.topLeft}>
        <div style={styles.time}>{hours}:{minutes}</div>
        <div style={styles.date}>{dayName.charAt(0).toUpperCase() + dayName.slice(1)} {dateFull}</div>
      </div>

      {/* Centre - Sélection utilisateur ou login */}
      <div style={styles.center}>
        {!selectedUser ? (
          // Écran de sélection d'utilisateur
          <div style={styles.userSelector}>
            <h2 style={styles.selectTitle}>Sélectionnez un utilisateur</h2>
            <div style={styles.userGrid}>
              {users.filter(u => u.username !== 'ymnode').map((user) => (
                <button
                  key={user.username}
                  onClick={() => selectUser(user)}
                  style={styles.userCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <div style={styles.userAvatar}>{user.username.charAt(0).toUpperCase()}</div>
                  <div style={styles.userName}>{user.username}</div>
                </button>
              ))}
            </div>
            <div style={styles.adminLoginSection}>
              <div style={styles.adminDivider}>ou</div>
              <button
                onClick={() => {
                  setSelectedUser('admin-manual');
                  setTimeout(() => usernameRef.current && usernameRef.current.focus(), 100);
                }}
                style={styles.adminButton}
                title="Connexion administrateur - Entrez les identifiants manuellement"
              >
                🔐 Connexion Administrateur
              </button>
              <div style={styles.securityNote}>Entrez vos identifiants manuellement pour plus de sécurité</div>
            </div>
          </div>
        ) : selectedUser === 'admin-manual' ? (
          // Écran de saisie manuelle pour admin
          <div style={styles.loginForm}>
            <div style={styles.userDisplayArea}>
              <div style={styles.adminIcon}>🔐</div>
              <h2 style={styles.adminTitle}>Connexion Administrateur</h2>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Nom d'utilisateur</label>
                <input
                  ref={usernameRef}
                  type="text"
                  placeholder="Entrez votre nom d'utilisateur"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  style={styles.inputField}
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Mot de passe</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    style={styles.passwordInput}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.showButton}
                    disabled={isLoading}
                  >
                    {showPassword ? '👁️' : '🔒'}
                  </button>
                </div>
              </div>

              {message && <div style={styles.error}>{message}</div>}

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  ...styles.signInButton,
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? '...' : '→'}
              </button>
            </form>

            <button
              onClick={() => {
                setSelectedUser(null);
                setUsername('');
                setPassword('');
                setMessage('');
              }}
              style={styles.backButton}
              disabled={isLoading}
            >
              Retour
            </button>
          </div>
        ) : (
          // Écran de saisie du mot de passe
          <div style={styles.loginForm}>
            <div style={styles.userDisplayArea}>
              <div style={styles.largeAvatar}>{username.charAt(0).toUpperCase()}</div>
              <h2 style={styles.userDisplayName}>{username}</h2>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.passwordGroup}>
                <input
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Entrez votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  style={styles.passwordField}
                  autoComplete="current-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.showButton}
                  disabled={isLoading}
                  title={showPassword ? 'Masquer' : 'Afficher'}
                >
                  {showPassword ? '👁️' : '🔒'}
                </button>
              </div>

              {message && <div style={styles.error}>{message}</div>}

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  ...styles.signInButton,
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? '...' : '→'}
              </button>
            </form>

            <button
              onClick={() => {
                setSelectedUser(null);
                setUsername('');
                setPassword('');
                setMessage('');
              }}
              style={styles.backButton}
              disabled={isLoading}
            >
              Autre utilisateur
            </button>
          </div>
        )}
      </div>

      {/* Bas - Info système */}
      <div style={styles.bottomRight}>
        <div style={styles.systemInfo}>My Dashboard v1.0</div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  background: {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e22ce 100%)`,
    backgroundSize: '400% 400%',
    animation: 'gradientShift 15s ease infinite'
  },
  topLeft: {
    position: 'absolute',
    top: 48,
    left: 48,
    color: 'white',
    zIndex: 10,
    textAlign: 'left'
  },
  time: {
    fontSize: 64,
    fontWeight: 200,
    letterSpacing: '-2px',
    lineHeight: 1,
    marginBottom: 8
  },
  date: {
    fontSize: 16,
    fontWeight: 300,
    opacity: 0.9
  },
  center: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  userSelector: {
    textAlign: 'center',
    animation: 'fadeIn 0.4s ease'
  },
  selectTitle: {
    color: 'white',
    fontSize: 24,
    fontWeight: 300,
    marginBottom: 48,
    letterSpacing: '0.5px'
  },
  userGrid: {
    display: 'flex',
    gap: 32,
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  userCard: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 16,
    padding: '24px 20px',
    minWidth: 120,
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12
  },
  userAvatar: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 600,
    color: 'white',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)'
  },
  userName: {
    color: 'white',
    fontSize: 14,
    fontWeight: 500
  },
  loginForm: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 24,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
  },
  userDisplayArea: {
    textAlign: 'center',
    marginBottom: 40
  },
  largeAvatar: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    fontWeight: 600,
    color: 'white',
    margin: '0 auto 16px',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)'
  },
  userDisplayName: {
    color: 'white',
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    letterSpacing: '0.5px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  passwordGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: '0 12px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    transition: 'all 0.2s ease'
  },
  passwordField: {
    flex: 1,
    padding: '14px 8px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'white',
    fontSize: 15,
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  showButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    padding: '8px 4px',
    color: 'rgba(255, 255, 255, 0.6)',
    transition: 'color 0.2s ease',
    display: 'flex',
    alignItems: 'center'
  },
  error: {
    padding: '12px 14px',
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    border: '1px solid rgba(239, 68, 68, 0.3)',
    textAlign: 'center'
  },
  signInButton: {
    padding: '14px 16px',
    fontSize: 24,
    fontWeight: 300,
    color: 'white',
    background: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: 8
  },
  backButton: {
    marginTop: 12,
    padding: '10px 16px',
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.7)',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: 500
  },
  adminNote: {
    marginTop: 32,
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.5)',
    textAlign: 'center'
  },
  bottomRight: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
    zIndex: 10
  },
  systemInfo: {
    opacity: 0.6,
    fontWeight: 300
  },
  adminLoginSection: {
    marginTop: 32,
    paddingTop: 32,
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center'
  },
  adminDivider: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
    marginBottom: 20
  },
  adminButton: {
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 500,
    color: 'white',
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    width: '100%',
    boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)'
  },
  securityNote: {
    marginTop: 24,
    padding: '12px 14px',
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: 8,
    fontSize: 12,
    color: 'rgba(147, 197, 253, 1)',
    lineHeight: '1.5'
  },
  adminIcon: {
    fontSize: 48,
    marginBottom: 12
  },
  adminTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
    letterSpacing: '0.5px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12
  },
  inputLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 13,
    fontWeight: 500,
    marginLeft: 2
  },
  inputField: {
    padding: '12px 14px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease'
  },
  passwordContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: '0 12px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    transition: 'all 0.2s ease'
  },
  passwordInput: {
    flex: 1,
    padding: '12px 8px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'white',
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  }
};

// Ajouter les animations CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  input::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;
if (document.head) {
  document.head.appendChild(styleSheet);
}
